import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EventLogEntry, ParserEventLogEntry, Sample, Test, Order } from '@mariana/labos-client';
import { Breadcrumb } from './Breadcrumb';
import { DashboardCard } from './DashboardCard';
import { EventsTable } from './EventsTable';
import { RecentEntityCard } from './RecentEntityCard';
import { StateBadge, resolveVariant } from './StateBadge';
import { limsClient } from '../api/limsClient';
import { useLab } from '../LabContext';
import { formatDate } from '../utils/formatDate';
import './Dashboard.css';

interface EntityCounts {
  samples: number;
  tests: number;
  assays: number;
  orders: number;
}

interface StatusBreakdown {
  [status: string]: number;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { labFilters, eventLabId } = useLab();
  const [counts, setCounts] = useState<EntityCounts>({ samples: 0, tests: 0, assays: 0, orders: 0 });
  const [sampleStatuses, setSampleStatuses] = useState<StatusBreakdown>({});
  const [testStatuses, setTestStatuses] = useState<StatusBreakdown>({});
  const [assayStatuses, setAssayStatuses] = useState<StatusBreakdown>({});
  const [orderStatuses, setOrderStatuses] = useState<StatusBreakdown>({});
  const [recentSamples, setRecentSamples] = useState<Sample[]>([]);
  const [recentTests, setRecentTests] = useState<Test[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [parserEvents, setParserEvents] = useState<ParserEventLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const lf = labFilters;
        const [samples, tests, assays, orders, recentSamplesRes, recentTestsRes, recentOrdersRes, eventsRes, parserEventsRes] = await Promise.all([
          limsClient.getSamples({ limit: 1, filters: lf.length > 0 ? lf : undefined }),
          limsClient.getTests({ limit: 1, filters: lf.length > 0 ? lf : undefined }),
          limsClient.getAssays({ limit: 1 }),
          limsClient.getOrders({ limit: 1, filters: lf.length > 0 ? lf : undefined }),
          limsClient.getSamples({ limit: 5, sort_by: 'id', sort_order: 'desc', filters: lf.length > 0 ? lf : undefined }),
          limsClient.getTests({ limit: 5, sort_by: 'id', sort_order: 'desc', filters: lf.length > 0 ? lf : undefined }),
          limsClient.getOrders({ limit: 5, sort_by: 'id', sort_order: 'desc', filters: lf.length > 0 ? lf : undefined }),
          limsClient.getEvents({ limit: 10, labId: eventLabId }),
          limsClient.getParserEvents({ limit: 5, labId: eventLabId }),
        ]);
        setCounts({
          samples: samples.pagination.count,
          tests: tests.pagination.count,
          assays: assays.pagination.count,
          orders: orders.pagination.count,
        });
        setRecentSamples(recentSamplesRes.data);
        setRecentTests(recentTestsRes.data);
        setRecentOrders(recentOrdersRes.data);
        setEvents(eventsRes.data);
        setParserEvents(parserEventsRes.data);

        const sampleFlags = ['complete', 'partially_complete', 'received'] as const;
        const testStates = ['NOT STARTED', 'IN PROGRESS', 'COMPLETED'];
        const orderStates = ['CREATED', 'RECEIVED', 'IN PROGRESS', 'COMPLETED'];
        const assayLabels = ['active', 'inactive'];

        const [sampleFlagResults, testStatusResults, orderStatusResults, assayFlagResults] = await Promise.all([
          Promise.allSettled(sampleFlags.map(f => limsClient.getSamples({ limit: 1, filters: [{ attribute_name: f, operator: '=', value: true }, ...lf] }))),
          Promise.allSettled(testStates.map(s => limsClient.getTests({ limit: 1, filters: [{ attribute_name: 'state', operator: '=', value: s }, ...lf] }))),
          Promise.allSettled(orderStates.map(s => limsClient.getOrders({ limit: 1, filters: [{ attribute_name: 'state', operator: '=', value: s }, ...lf] }))),
          Promise.allSettled([true, false].map(f => limsClient.getAssays({ limit: 1, filters: [{ attribute_name: 'active', operator: '=', value: f }] }))),
        ]);

        const extractCounts = (results: PromiseSettledResult<{ pagination: { count: number } }>[], labels: readonly string[]) => {
          const breakdown: StatusBreakdown = {};
          labels.forEach((label, i) => {
            if (results[i].status === 'fulfilled') {
              const count = (results[i] as PromiseFulfilledResult<{ pagination: { count: number } }>).value.pagination.count;
              if (count > 0) breakdown[label] = count;
            }
          });
          return breakdown;
        };

        setSampleStatuses(extractCounts(sampleFlagResults, sampleFlags));
        setTestStatuses(extractCounts(testStatusResults, testStates));
        setOrderStatuses(extractCounts(orderStatusResults, orderStates));
        setAssayStatuses(extractCounts(assayFlagResults, assayLabels));
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [labFilters, eventLabId]);

  const stats: Array<{ label: string; count: number; path: string; breakdown?: StatusBreakdown }> = [
    { label: 'Samples', count: counts.samples, path: '/sample', breakdown: sampleStatuses },
    { label: 'Tests', count: counts.tests, path: '/test', breakdown: testStatuses },
    { label: 'Assays', count: counts.assays, path: '/assay', breakdown: assayStatuses },
    { label: 'Orders', count: counts.orders, path: '/order', breakdown: orderStatuses },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Dashboard' }]} />
      </div>

      <div className="dashboard-stats">
        {stats.map(s => (
          <button key={s.label} className="dashboard-stat" onClick={() => navigate(s.path)}>
            <div className="dashboard-stat-info">
              <span className="dashboard-stat-count">{loading ? '-' : s.count.toLocaleString()}</span>
              <span className="dashboard-stat-label">{s.label}</span>
            </div>
            {s.breakdown && Object.keys(s.breakdown).length > 0 && (
              <span className="dashboard-stat-breakdown">
                {Object.entries(s.breakdown).map(([status, count]) => (
                  <span key={status} className={`state-badge ${resolveVariant(status)}`}>
                    {count.toLocaleString()} {status.toLowerCase()}
                  </span>
                ))}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="dashboard-recent-grid">
        <RecentEntityCard
          title="Recent Samples"
          entityType="sample"
          entities={recentSamples}
          loading={loading}
          renderName={(e) => e.attributes?.sample_name || '–'}
          renderStatus={(e) => e.attributes?.complete ? <StateBadge state="completed" /> : null}
          onViewAll={() => navigate('/sample')}
          onEntityClick={(id) => navigate(`/sample/${id}`)}
        />
        <RecentEntityCard
          title="Recent Tests"
          entityType="test"
          entities={recentTests}
          loading={loading}
          renderName={(e) => `Sample ${e.attributes?.sample_id || '–'}`}
          renderStatus={(e) => e.attributes?.state ? <StateBadge state={e.attributes.state} /> : null}
          onViewAll={() => navigate('/test')}
          onEntityClick={(id) => navigate(`/test/${id}`)}
        />
        <RecentEntityCard
          title="Recent Orders"
          entityType="order"
          entities={recentOrders}
          loading={loading}
          renderName={(e) => e.attributes?.submitted_by || '–'}
          renderStatus={(e) => e.attributes?.state ? <StateBadge state={e.attributes.state} /> : null}
          onViewAll={() => navigate('/order')}
          onEntityClick={(id) => navigate(`/order/${id}`)}
        />
      </div>

      {loading ? (
        <div className="events-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : events.length === 0 && parserEvents.length === 0 ? (
        <div className="dashboard-empty-activity">
          <span className="dashboard-empty-activity-text">No recent activity</span>
        </div>
      ) : (
        <div className="dashboard-activity-grid">
          {events.length > 0 && (
            <DashboardCard title="Recent Events" onViewAll={() => navigate('/events')}>
              <div className="dashboard-events-wrapper">
                <EventsTable
                  events={events}
                  compact
                  onEntityClick={(type, id) => navigate(`/${type}/${id}`)}
                />
              </div>
            </DashboardCard>
          )}

          {parserEvents.length > 0 && (
            <DashboardCard title="Recent Parser Events" onViewAll={() => navigate('/parser-logs')}>
              <div className="dashboard-events-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Timestamp</th>
                      <th>Parser</th>
                      <th>File Name</th>
                      <th>Status</th>
                      <th>Matched / Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parserEvents.map(pe => {
                      const matched = pe.result?.matchedTests?.length ?? 0;
                      const updated = pe.result?.updatedTests ?? 0;
                      return (
                        <tr key={pe.id} className="parser-event-row" onClick={() => navigate('/parser-logs')} style={{ cursor: 'pointer' }}>
                          <td>{pe.id}</td>
                          <td>{formatDate(pe.createdAt)}</td>
                          <td>{pe.parserName}</td>
                          <td>{pe.fileName}</td>
                          <td>
                            <span className={`action-badge ${pe.status === 'success' ? 'updated' : pe.status === 'error' ? 'deleted' : 'created'}`}>
                              {pe.status}
                            </span>
                          </td>
                          <td>{matched} / {updated}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </DashboardCard>
          )}
        </div>
      )}
    </div>
  );
};
